import { toast } from 'react-toastify';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse, IApiQueryOptions } from 'api/ApiClient';
import { SuccessToastContainer } from 'components/Toast/ToastContainer';
import { ApiQueryOperator, IAction, IApiList, IPayout, PayoutApprovalStatus, PayoutStatus } from 'types';
import { formatErrorMessages } from 'utils/errors';
import * as API from '../../../api';
import * as Actions from './actions';
import { ICancelPayout, IGetEmployeePayouts, IGetSiteEmployeePayouts, IGetSitePayouts, IRetryPayout, Payouts } from './types';

const reviewPayoutsQuery = [
  { key: 'approval_check.status', value: PayoutApprovalStatus.REVIEW_REQUIRED, operator: ApiQueryOperator.eq },
  { key: 'status', value: [PayoutStatus.PENDING, PayoutStatus.PAYROLL], operator: ApiQueryOperator.in },
];

export function* getSitePayouts({ payload }: IAction<Payouts, IGetSitePayouts>) {
  try {
    const { siteId, options } = payload;
    const response: ApiResponse<IApiList<IPayout>> = yield call(API.Sites.getPayouts, siteId, options);

    yield put(Actions.getSitePayoutsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getSitePayoutsError(error));
  }
}

export function* getSiteEmployeePayouts({ payload }: IAction<Payouts, IGetSiteEmployeePayouts>) {
  try {
    const { siteId, employeeId, options } = payload;
    const response: ApiResponse<IApiList<IPayout>> = yield call(API.Sites.getEmployeePayouts, siteId, employeeId, options);

    yield put(Actions.getSiteEmployeePayoutsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getSiteEmployeePayoutsError(error));
  }
}

export function* getSiteReviewPayouts({ payload }: IAction<Payouts, IGetSitePayouts>) {
  try {
    const { siteId, options } = payload;
    const response: ApiResponse<IApiList<IPayout>> = yield call(API.Sites.getPayouts, siteId, { ...options, q: [...reviewPayoutsQuery, ...(options.q || [])] });

    yield put(Actions.getSiteReviewPayoutsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getSiteReviewPayoutsError(error));
  }
}

export function* getSiteReviewPayoutsCount({ payload }: IAction<Payouts, { siteId: string }>) {
  try {
    const { siteId } = payload;
    const response: ApiResponse<IApiList<IPayout>> = yield call(API.Sites.getPayouts, siteId, {
      limit: 1,
      q: reviewPayoutsQuery,
    });

    yield put(Actions.getSiteReviewPayoutsCountSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getSiteReviewPayoutsCountError(error));
  }
}

export function* getEmployeePayouts({ payload }: IAction<Payouts, IGetEmployeePayouts>) {
  try {
    const { employeeId, options } = payload;
    const response: ApiResponse<IApiList<IPayout>> = yield call(API.Employees.listPayouts, employeeId, options);

    yield put(Actions.getEmployeePayoutsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getEmployeePayoutsError(error));
  }
}

export function* getUserPayouts({ payload }: IAction<Payouts, { options?: IApiQueryOptions }>) {
  try {
    const { options } = payload;
    const response: ApiResponse<IApiList<IPayout>> = yield call(API.Users.listPayouts, options);

    yield put(Actions.getUserPayoutsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getUserPayoutsError(error));
  }
}

export function* retryPayout({ payload }: IAction<Payouts, IRetryPayout>) {
  try {
    const { payoutId, onSuccess } = payload;
    const response: ApiResponse<IPayout> = yield call(API.Payments.retryPayout, payoutId);

    yield put(Actions.retryPayoutSuccess(response.body));

    yield call(toast, SuccessToastContainer({ message: 'Payment retry successful' }));

    // Execute on success function
    if (onSuccess) yield call(onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });
    yield put(Actions.retryPayoutError(formattedError));
  }
}

export function* cancelPayout({ payload }: IAction<Payouts, ICancelPayout>) {
  try {
    const { payoutId, body, onSuccess } = payload;
    const response: ApiResponse<IPayout> = yield call(API.Payments.cancelPayout, payoutId, body);

    yield put(Actions.cancelPayoutSuccess(response.body));

    yield call(toast, SuccessToastContainer({ message: 'Payment cancellation successful' }));

    // Execute on success function
    if (onSuccess) yield call(onSuccess);
  } catch (error: any) {
    const formattedError = formatErrorMessages({
      errors: error.body?.errors,
      statusCode: error.statusCode,
    });
    yield put(Actions.cancelPayoutError(formattedError));
  }
}

export function* watchGetSitePayouts() {
  yield takeEvery(Payouts.GET_SITE_PAYOUTS_REQUEST, getSitePayouts);
}

export function* watchGetSiteEmployeePayouts() {
  yield takeEvery(Payouts.GET_SITE_EMPLOYEE_PAYOUTS_REQUEST, getSiteEmployeePayouts);
}

export function* watchGetSiteReviewPayouts() {
  yield takeEvery(Payouts.GET_SITE_REVIEW_PAYOUTS_REQUEST, getSiteReviewPayouts);
}

export function* watchGetSiteReviewPayoutsCount() {
  yield takeEvery(Payouts.GET_SITE_REVIEW_PAYOUTS_COUNT_REQUEST, getSiteReviewPayoutsCount);
}

export function* watchGetEmployeePayouts() {
  yield takeEvery(Payouts.GET_EMPLOYEE_PAYOUTS_REQUEST, getEmployeePayouts);
}

export function* watchGetUserPayouts() {
  yield takeEvery(Payouts.GET_USER_PAYOUTS_REQUEST, getUserPayouts);
}

export function* watchRetryPayout() {
  yield takeEvery(Payouts.RETRY_PAYOUT_REQUEST, retryPayout);
}

export function* watchCancelPayout() {
  yield takeEvery(Payouts.CANCEL_PAYOUT_REQUEST, cancelPayout);
}

function* payoutsSagas() {
  yield all([
    fork(watchGetSitePayouts),
    fork(watchGetSiteEmployeePayouts),
    fork(watchGetSiteReviewPayouts),
    fork(watchGetSiteReviewPayoutsCount),
    fork(watchGetEmployeePayouts),
    fork(watchGetUserPayouts),
    fork(watchRetryPayout),
    fork(watchCancelPayout),
  ]);
}
export { payoutsSagas };
